import * as React from "react";
import { Panel, PrimaryButton, Stack, Persona, PersonaSize, Label, ILabelStyles, FontSizes, TextField, ActionButton, FontWeights, Dropdown, ResponsiveMode } from "@fluentui/react";
import authModule from "../../../../utilities/AuthModule";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { profilePanelStyles } from "../../config/controlStyles";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../redux";
import { Toggle } from "@fluentui/react/lib/Toggle";
import { updateUserSettings } from "../../redux/modules/user"
import { IUserProfile, IUserSettings } from "../../interfaces/IUserProfile";
// import { getMailSettings, SaveMailSettings } from "../../../../utilities/SaveOnSendUtils";
import { ModeEnum } from "../enums/modeEnum";
import { getUserProfilePicture } from "../../services/GraphService";
import EmailMappingPanel from "../panels/EmailMappingPanel";
import generalModule from "../../../../utilities/GeneralModule";
import { logger } from "../../../../utilities/logger";
import { setContentManagerExchangeAdditionalMailboxes } from "../../redux/modules/contentManagerDirectory";
import { getExchangeFolders } from "../../services/ExchangeService";
import Loader from "../Loader";
import { CMConfirmModal } from "../../../../contentManager/Panels/CMConfirmModal";
import { LandingPageEnum } from "../enums/LandingPageEnum";
import { apiGetAdditionalMailboxes, apiSetAdditionalMailboxes } from "../../services/ApiService";
import { getLocalStorage, localStorageKeys, setLocalStorage } from "../helpers/localStorageHelper";

const preferencesLabelStyle: ILabelStyles = {
    root: {
        fontSize: FontSizes.size18,
        lineHeight: "24px",
    }
};

const mailboxTextStyles: Partial<ILabelStyles> = {
    root: {
        fontWeight: FontWeights.regular
    }
}

const mapDispatchToProps = {
    updateUserSettings,
    setContentManagerExchangeAdditionalMailboxes
};
const mapStateToProps = (state: RootState) => {
    return {
        mode: state.mode.mode,
        userSettings: state.user.settings,
        account: state.user.account,
        additionalMailboxes: state.contentManagerDirectory?.exchange?.additionalMailboxes,
        enableExchange: state.tenantInfo.enableExchange,
        isTessEnabled: state.tenantInfo.isTessEnabled
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface UserSettingsPanelProps extends PropsFromRedux {
    isOpen: boolean,
    closePanel: () => void,
    profileImageUrl?: string,
}

const UserSettingsPanel: React.FunctionComponent<UserSettingsPanelProps> = ({
    updateUserSettings,
    userSettings,
    account,
    mode,
    isOpen,
    closePanel,
    profileImageUrl,
    setContentManagerExchangeAdditionalMailboxes,
    additionalMailboxes,
    enableExchange,
    isTessEnabled
}: UserSettingsPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [showPinnedToggle, setShowPinnedToggle] = React.useState(userSettings.showPinned);
    const [showRecentToggle, setShowRecentToggle] = React.useState(userSettings.showRecent);
    const [showSharedToggle, setShowSharedToggle] = React.useState(userSettings.showShared);
    const [landingPage, setLandingPage] = React.useState(userSettings.landingPage);
    const [autoSelectAttachments, setAutoSelectAttachments] = React.useState(userSettings.autoSelectAttachments);
    const [autoRemoveMailItemAttachments, setAutoRemoveMailItemAttachments] = React.useState(userSettings.autoRemoveMailItemAttachments);
    const [convertEmailToPDF, setConvertEmailToPDF] = React.useState(userSettings.convertEmailToPDF);
    const [isAutoOpenSupported] = React.useState(!generalModule.isInConentMangerMode() && Office.context.requirements.isSetSupported("AddinCommands", "1.1") && Office.context.document && Number((new URLSearchParams(window.location.search)).get("v")) > 5.1);
    const [autoOpenDocubird, setAutoOpenDocubird] = React.useState(isAutoOpenSupported && Office.context.document.settings.get("Office.AutoShowTaskpaneWithDocument"));
    const [userProfileImageUrl, setUserProfileImageUrl] = React.useState<string>(undefined);
    const [isEmailMappingPanelOpen, setIsEmailMappingPanelOpen] = React.useState<boolean>(false);

    const [addSharedMailbox, setAddSharedMailbox] = React.useState(false);
    const [isAuthorizingMailbox, setIsAuthorizingMailbox] = React.useState(false);
    const [sharedMailboxName, setSharedMailboxName] = React.useState("");
    const [sharedMailboxNameToDelete, setSharedMailboxNameToDelete] = React.useState("");
    const [mailboxError, setMailboxError] = React.useState("");
    const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = React.useState(false);

    const landingPageOptions = [
        { key: LandingPageEnum.Default, text: languageStrings.Default },
        ...(userSettings.enableSharePoint !== false ? [{ key: LandingPageEnum.SharePoint, text: "SharePoint" }] : []),
        ...(userSettings.enableTeams !== false ? [{ key: LandingPageEnum.Teams, text: "Teams" }] : []),
        ...(userSettings.enableOneDrive !== false ? [{ key: LandingPageEnum.OneDrive, text: "OneDrive" }] : []),
        ...(enableExchange !== false ? [{ key: LandingPageEnum.Exchange, text: "Exchange" }] : []),
        ...(isTessEnabled ? [{ key: LandingPageEnum.Tess, text: "Tess" }] : [])
    ]

    React.useEffect(() => {
        const setupAsyncWrapper = async () => {
            let profilePictureUrl: string = profileImageUrl;
            if (!profilePictureUrl) {
                profilePictureUrl = await getUserProfilePicture(await authModule.getGraphAccessToken());
            }
            setUserProfileImageUrl(profilePictureUrl ?? undefined);

            if (generalModule.isInConentMangerMode() && enableExchange && additionalMailboxes === undefined) {
                try {
                    const mailboxes = await apiGetAdditionalMailboxes(account.accountId);
                    setContentManagerExchangeAdditionalMailboxes(mailboxes?.value?.filter(x => x?.length > 0))
                } catch (error) {
                    logger(error, "UserSettingsPanel.tsx (getAdditionalMailboxes)");
                }
            }
        };
        setupAsyncWrapper();
    }, [account.accountId, enableExchange]);

    const saveAndClose = () => {
        const dataChanged: boolean = !(showPinnedToggle == userSettings.showPinned &&
            showRecentToggle == userSettings.showRecent &&
            showSharedToggle == userSettings.showShared &&
            landingPage == userSettings.landingPage &&
            autoSelectAttachments == userSettings.autoSelectAttachments &&
            autoRemoveMailItemAttachments == userSettings.autoRemoveMailItemAttachments &&
            convertEmailToPDF == userSettings.convertEmailToPDF
        );
        if (dataChanged) {
            const settings: IUserSettings = {
                ...userSettings,
                showPinned: showPinnedToggle,
                showRecent: showRecentToggle,
                showShared: showSharedToggle,
                landingPage: landingPage,
                autoSelectAttachments: autoSelectAttachments,
                autoRemoveMailItemAttachments: autoRemoveMailItemAttachments,
                convertEmailToPDF: convertEmailToPDF
            }
            updateUserSettings(account.accountId, settings);

            const cachedUserProfile: IUserProfile = getLocalStorage(localStorageKeys.userProfile);
            if (cachedUserProfile) {
                setLocalStorage(localStorageKeys.userProfile, { ...cachedUserProfile, settings });
            }
        }
        closePanel();
    }

    const onAddSharedMailbox = async (mailboxName: string) => {
        try {
            setIsAuthorizingMailbox(true);
            const folders = await fetchFolders(mailboxName);
            if (folders.length > 0) {
                await apiSetAdditionalMailboxes(account.accountId, [...additionalMailboxes, sharedMailboxName]);

                setContentManagerExchangeAdditionalMailboxes([...additionalMailboxes, sharedMailboxName]);
                setAddSharedMailbox(false);
                setSharedMailboxName("");
                setMailboxError("");
                setIsAuthorizingMailbox(false);
            } else {
                setIsAuthorizingMailbox(false);
                setMailboxError("Authorization failed");
            }
        } catch (error) {
            logger(error, "UserSettingsPanel.tsx (onAddSharedMailbox)");
        }
    }

    const validateMailboxName = () => {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        const isValidEmail = emailRegex.test(sharedMailboxName);
        if (isValidEmail) {
            const isDuplicate = additionalMailboxes.some(x => x.toLowerCase() === sharedMailboxName.toLowerCase()) || sharedMailboxName.toLowerCase() === account.username.toLowerCase()
            if (isDuplicate) {
                setMailboxError(languageStrings.MailboxAlreadyAdded);

            } else {
                onAddSharedMailbox(sharedMailboxName);
            }
        } else {
            setMailboxError(languageStrings.InvalidEmail);
        }
    }

    const fetchFolders = async (accountUsername: string) => {
        try {
            return await getExchangeFolders(accountUsername, undefined, 1, false);
        } catch (error) {
            logger(error, "UserSettingsPanel.tsx (fetchFolders)");
            return [];
        }
    }

    return (
        <Panel
            isOpen={isOpen}
            closeButtonAriaLabel="Close"
            headerText={languageStrings.Account}
            styles={profilePanelStyles}
            isHiddenOnDismiss={true}
            onDismiss={saveAndClose}
        >
            <div className="xb-setting-container">
                <div style={{ marginTop: 10, marginBottom: 18 }}>
                    <Stack tokens={{ childrenGap: 10 }}>
                        <Persona
                            imageUrl={userProfileImageUrl}
                            text={account?.displayName}
                            secondaryText={account?.username}
                            size={PersonaSize.size40}
                        />
                    </Stack>
                    <PrimaryButton
                        text={languageStrings.Disconnect}
                        className="logout"
                        onClick={() => {
                            closePanel();
                            authModule.signOut();
                        }}
                        allowDisabledFocus
                    />
                </div>
                <hr />
                <Label styles={preferencesLabelStyle}>
                    {languageStrings.Preferences}
                </Label>
                {isOpen && <Stack>
                    <Toggle
                        label={languageStrings.ShowPinned}
                        checked={showPinnedToggle}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                        onChange={() => setShowPinnedToggle(!showPinnedToggle)}
                    />
                    <Toggle
                        label={languageStrings.ShowRecent}
                        checked={showRecentToggle}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                        onChange={() => setShowRecentToggle(!showRecentToggle)}
                    />
                    <Toggle
                        label={languageStrings.ShowShared}
                        checked={showSharedToggle}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                        onChange={() => setShowSharedToggle(!showSharedToggle)}
                    />
                    <Stack.Item>
                        <Label>{languageStrings.LandingPage}</Label>
                        <Dropdown
                            selectedKey={landingPage && landingPageOptions.some(x => x.key === landingPage) ? landingPage.valueOf() : landingPageOptions[0].key}
                            onChange={(_e, o) => setLandingPage(Number(o.key))}
                            options={landingPageOptions}
                        // responsiveMode={ResponsiveMode.small}
                        />
                    </Stack.Item>
                </Stack>}
                {isOpen && mode !== ModeEnum.office && !generalModule.isInConentMangerMode() &&
                    <>
                        <Label styles={preferencesLabelStyle} style={{ marginTop: 10 }}>
                            {languageStrings.OutlookSettings}
                        </Label>
                        <Stack>
                            <Toggle
                                label={languageStrings.AutoSelectAttachments}
                                checked={autoSelectAttachments}
                                onText={languageStrings.Yes}
                                offText={languageStrings.No}
                                onChange={() => setAutoSelectAttachments(!autoSelectAttachments)}
                            />
                            <Toggle
                                label={languageStrings.AutoRemoveMailItemAttachments}
                                checked={autoRemoveMailItemAttachments}
                                onText={languageStrings.Yes}
                                offText={languageStrings.No}
                                onChange={() => setAutoRemoveMailItemAttachments(!autoRemoveMailItemAttachments)}
                            />
                            <Toggle
                                label={languageStrings.ConvertEmailToPdf}
                                checked={convertEmailToPDF}
                                onText={languageStrings.Yes}
                                offText={languageStrings.No}
                                onChange={() => setConvertEmailToPDF(!convertEmailToPDF)}
                            />
                            <Label>
                                {languageStrings.EmailHeaderMapping}
                            </Label>
                            <PrimaryButton
                                onClick={() => {
                                    setIsEmailMappingPanelOpen(true)
                                }}
                            >
                                {languageStrings.Manage}
                            </PrimaryButton>
                        </Stack>
                    </>}
                {isOpen && mode === ModeEnum.office && isAutoOpenSupported &&
                    <>
                        <Label styles={preferencesLabelStyle} style={{ marginTop: 10 }}>
                            {languageStrings.DocumentSettings}
                        </Label>
                        <Stack>
                            <Toggle
                                label={languageStrings.AutoOpenDocubird}
                                checked={autoOpenDocubird}
                                onText={languageStrings.Yes}
                                offText={languageStrings.No}
                                onChange={async (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean) => {
                                    setAutoOpenDocubird(checked ?? false);

                                    try {
                                        Office.context.document.settings.set("Office.AutoShowTaskpaneWithDocument", checked ?? false);
                                        Office.context.document.settings.saveAsync();

                                        if (Office.context.requirements.isSetSupported("WordApi", "1.1")) {
                                            await Word.run(async (context) => {
                                                if (context.document) {
                                                    context.document.save();
                                                    await context.sync();
                                                }
                                            });
                                        }
                                        else if (Office.context.requirements.isSetSupported("ExcelApi", "1.11")) {
                                            await Excel.run(async (context) => {
                                                if (context.workbook) {
                                                    context.workbook.save(Excel.SaveBehavior.save);
                                                    await context.sync();
                                                }
                                            });
                                        }
                                    } catch (error) {
                                        logger(error, "UserSettingsPanel.tsx - AutoOpenDocubird (onChange)");
                                    }
                                }}
                            />
                        </Stack>
                    </>}
                {isOpen && generalModule.isInConentMangerMode() && enableExchange &&
                    <>
                        <div style={{ marginTop: 10, marginBottom: 5 }}>
                            <Label styles={preferencesLabelStyle}>
                                {languageStrings.ExchangeSettings}
                            </Label>
                            <Toggle
                                label={languageStrings.AutoSelectAttachments}
                                checked={autoSelectAttachments}
                                onText={languageStrings.Yes}
                                offText={languageStrings.No}
                                onChange={() => setAutoSelectAttachments(!autoSelectAttachments)}
                            />
                            <Label>
                                {languageStrings.SharedMailboxes}
                            </Label>
                            <Stack>
                                {!addSharedMailbox && !isAuthorizingMailbox && <ActionButton text={languageStrings.Add} iconProps={{ iconName: "Add" }} styles={{ root: { height: 40 } }} onClick={() => setAddSharedMailbox(true)} />}
                                {addSharedMailbox && !isAuthorizingMailbox && <Stack horizontal verticalAlign="center">
                                    <Stack.Item grow style={{ paddingTop: 3 }}>
                                        <TextField
                                            errorMessage={mailboxError}
                                            placeholder={languageStrings.MailboxAddress}
                                            value={sharedMailboxName}
                                            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => setSharedMailboxName(newValue)}
                                            onKeyDown={async (event) => {
                                                setMailboxError("");
                                                if (event.key === "Enter") {
                                                    validateMailboxName();
                                                }
                                            }}
                                        />
                                    </Stack.Item>
                                    <Stack.Item>
                                        <ActionButton iconProps={{ iconName: "Cancel" }} onClick={() => {
                                            setMailboxError("");
                                            setAddSharedMailbox(false);
                                            setSharedMailboxName("");
                                        }} />
                                    </Stack.Item>
                                    <Stack.Item>
                                        <ActionButton disabled={!sharedMailboxName} iconProps={{ iconName: "Accept" }} onClick={() => {
                                            validateMailboxName();
                                        }} />
                                    </Stack.Item>
                                </Stack>}
                                {isAuthorizingMailbox &&
                                    <Loader label={languageStrings.MailboxAuthorizing} />
                                }
                                {additionalMailboxes?.length > 0 && additionalMailboxes.map((mailbox, index) => {
                                    return (
                                        <Stack key={index} horizontal verticalAlign="center" className="additionalMailboxItem">
                                            <Stack.Item grow><Label styles={mailboxTextStyles}>{mailbox}</Label></Stack.Item>
                                            <ActionButton iconProps={{ iconName: "Delete" }} onClick={() => {
                                                setSharedMailboxNameToDelete(mailbox);
                                                setShowConfirmDeleteDialog(true);
                                            }} />
                                        </Stack>
                                    )
                                })}
                            </Stack>
                        </div>
                        <Label>
                            {languageStrings.EmailHeaderMapping}
                        </Label>
                        <PrimaryButton
                            onClick={() => {
                                setIsEmailMappingPanelOpen(true)
                            }}
                        >
                            {languageStrings.Manage}
                        </PrimaryButton>
                    </>}
            </div>
            <EmailMappingPanel
                isOpen={isEmailMappingPanelOpen}
                closePanel={() => setIsEmailMappingPanelOpen(false)}
            />
            {showConfirmDeleteDialog && <CMConfirmModal
                title={`${languageStrings.Confirm} ${languageStrings.Delete}`}
                message={languageStrings.ConfirmDeleteMailbox.replace("{mailbox}", sharedMailboxNameToDelete)}
                showDialog={showConfirmDeleteDialog}
                closeDialog={() => {
                    setSharedMailboxNameToDelete("");
                    setShowConfirmDeleteDialog(false)
                }}
                closeText=""
                confirmText={languageStrings.Ok}
                onConfirm={async () => {
                    setShowConfirmDeleteDialog(false);
                    try {
                        const newMailboxes = [...additionalMailboxes.filter(x => x !== sharedMailboxNameToDelete)];
                        await apiSetAdditionalMailboxes(account.accountId, newMailboxes);
                        setContentManagerExchangeAdditionalMailboxes(newMailboxes);
                        setSharedMailboxNameToDelete("");
                    } catch (error) {
                        logger(error, "UserSettingsPanel.tsx (setAdditionalMailboxes)");
                    }
                }}
            />}
        </Panel>);
};

export default React.memo(connector(UserSettingsPanel));